import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, RefreshCw, CreditCard, HelpCircle, Dog } from 'lucide-react';
import { Button } from "@/components/ui/button";
import toast from 'react-hot-toast';

const Failure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(15);
  const errorCode = searchParams.get('error') || 'unknown';
  const errorMessage = getErrorMessage(errorCode);
  
  // Handle redirection back to pricing
  const redirectToPricing = () => {
    navigate('/dog-analyzer');
  };

  // Set up countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(timer);
          redirectToPricing();
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
    
    // Cleanup function
    return () => clearInterval(timer);
  }, []);

  // Show toast notification on mount
  useEffect(() => {
    toast.error('El pago no pudo ser procesado', {
      duration: 5000,
      style: {
        background: '#FEF2F2',
        color: '#991B1B',
        padding: '16px',
        borderRadius: '8px',
      }
    });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-red-50 to-white overflow-hidden px-4 py-6 sm:px-0 sm:py-0">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 1 }}
          className="absolute -top-20 -right-20 w-40 h-40 sm:w-64 sm:h-64 rounded-full bg-red-200/20 blur-3xl"
        />
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute -bottom-32 -left-20 w-60 h-60 sm:w-80 sm:h-80 rounded-full bg-orange-200/20 blur-3xl"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full mx-auto p-5 sm:p-8 bg-white rounded-xl shadow-lg relative z-10"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="mx-auto mb-4 sm:mb-6 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-red-100 to-red-50 rounded-full flex items-center justify-center"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, 0, -5, 0]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500" />
            </motion.div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent"
          >
            Pago No Procesado
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6"
          >
            Lo sentimos, hubo un problema al procesar tu pago.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-100"
          >
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
              <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
              <h3 className="text-sm sm:text-base font-medium text-red-800">
                ¿Qué salió mal?
              </h3>
            </div>
            
            <p className="text-xs sm:text-sm text-gray-600 mb-3">
              {errorMessage}
            </p>
            
            <div className="flex flex-col gap-2 mt-3">
              <div className="flex items-center justify-between p-2 bg-white/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-red-500" />
                  <span className="text-xs sm:text-sm text-gray-700">Verifica los datos de tu tarjeta</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-white/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-red-500" />
                  <span className="text-xs sm:text-sm text-gray-700">Intenta con otro método de pago</span>
                </div>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="relative h-2 bg-gray-100 rounded-full overflow-hidden mb-2 sm:mb-3"
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(15 - countdown) / 15 * 100}%` }}
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-red-400 to-orange-400"
            />
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6"
          >
            Serás redirigido automáticamente en <span className="font-semibold text-red-600">{countdown}</span> segundos
          </motion.p>
          
          <div className="grid grid-cols-2 gap-3">
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={redirectToPricing}
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Volver</span>
            </motion.button>
            
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                // Set the flag before redirecting
                sessionStorage.setItem('retryPayment', 'true');
                navigate('/dog-analyzer');
              }}
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors shadow-md flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Reintentar</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Helper function to get error message based on error code
function getErrorMessage(errorCode) {
  const errorMessages = {
    'card_declined': 'Tu tarjeta fue rechazada. Por favor, verifica los datos o intenta con otra tarjeta.',
    'expired_card': 'Tu tarjeta ha expirado. Por favor, utiliza una tarjeta válida.',
    'incorrect_cvc': 'El código de seguridad (CVC) es incorrecto. Verifica e intenta nuevamente.',
    'processing_error': 'Ocurrió un error al procesar el pago. Por favor, intenta nuevamente más tarde.',
    'insufficient_funds': 'Fondos insuficientes en la tarjeta. Por favor, utiliza otra tarjeta.',
    'invalid_card': 'La información de la tarjeta es inválida. Verifica los datos e intenta nuevamente.',
    'unknown': 'Ocurrió un error inesperado. Por favor, intenta nuevamente o contacta a soporte.'
  };
  
  return errorMessages[errorCode] || errorMessages.unknown;
}

export default Failure; 